'use client'

import React, { useMemo } from 'react'
import moment from 'moment'
import { $DCprimaryColor, $DCWarningColor, DcChart } from 'pixel-base'
import { formatDateForApi } from 'pixel-base'
import { AjaxResponse } from 'rxjs/ajax'
import { getGetAgingOrdersData } from 'utils/api-routes-analytics'
import {
  axisPointerStyle,
  tooltipPosition,
  tooltipStyle,
  tooltipTrigger,
} from './chartStyles'

interface AgingData {
  green_count: string
  yellow_count: string
  red_count: string
}

interface OrderAgingProps {
  filters: {
    start_date: Date
    end_date: Date
    data_type: string
  }
  height?: number | string
  theme?: string[]
  setComponent: (_component: string) => void
  setShowDetail: (_showDetail: boolean) => void
  width?: string
}

interface TooltipParams {
  name: string
  value: number
  percent: number
}

const OrdersAgingChart: React.FC<OrderAgingProps> = ({
  filters,
  setComponent,
  setShowDetail,
  width,
}) => {
  const [data, setData] = React.useState<AgingData[]>([])
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    setLoading(true)
    const subscription = getGetAgingOrdersData({
      start_date: formatDateForApi(filters.start_date),
      end_date: formatDateForApi(filters.end_date),
    }).subscribe({
      next: (response: AjaxResponse<any>) => {
        try {
          const responseData = response?.response as { data: AgingData[] }
          setData(responseData?.data || [])
        } catch (err) {
          console.error('Error parsing aging orders data:', err)
          setData([])
        }
        setLoading(false)
      },
      error: (error) => {
        console.error('Error fetching aging orders data:', error)
        setData([])
        setLoading(false)
      },
    })

    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  }, [filters.start_date, filters.end_date])

  const pieChartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        name: 'Orders Aging',
        series: [],
        radius: ['40%', '70%'],
        center: ['50%', '50%'],
        label: {
          formatter: '{b}: {c} ({d}%)',
          fontSize: 14,
        },
      }
    }

    const firstData = data[0]
    if (!firstData) {
      return {
        name: 'Orders Aging',
        series: [],
        radius: ['40%', '70%'],
        center: ['50%', '50%'],
        label: {
          formatter: '{b}: {c} ({d}%)',
          fontSize: 14,
        },
      }
    }

    const { green_count, yellow_count, red_count } = firstData

    return {
      name: 'Orders Aging',
      series: [
        {
          value: Number(green_count) || 0,
          name: 'Shipped within 3 days',
          itemStyle: { color: $DCprimaryColor },
        },
        {
          value: Number(yellow_count) || 0,
          name: 'Shipped between 4-5 days',
          itemStyle: { color: $DCWarningColor },
        },
        {
          value: Number(red_count) || 0,
          name: 'Shipped after 5 days',
          itemStyle: { color: $DCWarningColor },
        },
      ],
      radius: ['40%', '70%'],
      center: ['50%', '50%'],
      label: {
        formatter: '{b}: {c} ({d}%)',
        fontSize: 14,
      },
    }
  }, [data])

  return (
    <DcChart
      isLoading={loading || false}
      isTitleClickable={true}
      title="Orders Aging"
      onClickTitle={() => {
        setComponent('ordersAgingChart')
        setShowDetail(true)
      }}
      data-testid="chart"
      type="pie"
      data={pieChartData}
      height={'350px'}
      tooltip={{
        trigger: tooltipTrigger,
        position: tooltipPosition,
        formatter: (params: TooltipParams) => {
          const { name, value, percent } = params
          return `${name}: ${value} (${percent}%)`
        },
        axisPointer: axisPointerStyle,
        backgroundColor: tooltipStyle.backgroundColor,
        borderColor: tooltipStyle.borderColor,
        borderWidth: tooltipStyle.borderWidth,
        extraCssText: tooltipStyle.extraCssText,
        textStyle: tooltipStyle.textStyle,
      }}
    />
  )
}

export default OrdersAgingChart
